import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { parseCoverAmountInput } from "../../common/utils/formatters";
import {
  commitRiderCoverInput,
  getEffectiveRiderSaBounds,
  quantizeRiderSa,
  RIDER_ID_ADD_PLUS,
} from "../../common/utils/riderCoverRules";
import { RIDER_COVER_PREMIUM_DEBOUNCE_MS } from "../../constants";

/**
 * Debounced rider cover text entry, clamp vs life cover, submit resolution, and premium refetch via handleRiderCoverChange.
 */
export function useQuoteRiders({
  selectedRiders,
  riderCovers,
  handleRiderCoverChange,
  syncRiderCovers,
  effectiveCoverAmount,
  markUserEditedQuote,
  ridersData,
}) {
  const [riderCoverDrafts, setRiderCoverDrafts] = useState({});
  const [riderCoverErrors, setRiderCoverErrors] = useState({});
  const riderCoverDebounceTimersRef = useRef({});
  const riderCoverDraftsLatestRef = useRef({});

  useEffect(
    () => () => {
      Object.values(riderCoverDebounceTimersRef.current).forEach((t) =>
        clearTimeout(t),
      );
      riderCoverDebounceTimersRef.current = {};
    },
    [],
  );

  useEffect(() => {
    if (effectiveCoverAmount == null) return;
    const patch = {};
    selectedRiders.forEach((id) => {
      if (id === RIDER_ID_ADD_PLUS) {
        if (riderCovers[id] !== effectiveCoverAmount) {
          patch[id] = effectiveCoverAmount;
        }
        return;
      }
      const cur = riderCovers[id];
      if (cur == null) return;
      const { min, max } = getEffectiveRiderSaBounds(id, effectiveCoverAmount);
      const q = quantizeRiderSa(cur, min, max);
      if (q != null && q !== cur) patch[id] = q;
    });
    if (Object.keys(patch).length === 0) return;
    syncRiderCovers(patch);
    setRiderCoverDrafts((prev) => {
      const next = { ...prev };
      Object.entries(patch).forEach(([k, v]) => {
        next[k] = String(v);
      });
      riderCoverDraftsLatestRef.current = {
        ...riderCoverDraftsLatestRef.current,
        ...next,
      };
      return next;
    });
  }, [effectiveCoverAmount, selectedRiders, riderCovers, syncRiderCovers]);

  const flushRiderCoverCommit = useCallback(
    (riderId) => {
      if (!selectedRiders.has(riderId) || riderId === RIDER_ID_ADD_PLUS) return;
      const draft =
        riderCoverDraftsLatestRef.current[riderId] ??
        riderCoverDrafts[riderId] ??
        (riderCovers[riderId] != null ? String(riderCovers[riderId]) : "");
      const res = commitRiderCoverInput(
        draft,
        riderId,
        effectiveCoverAmount,
        parseCoverAmountInput,
      );
      if (!res.ok) {
        setRiderCoverErrors((prev) => ({ ...prev, [riderId]: res.error }));
        return;
      }
      setRiderCoverErrors((prev) => {
        const next = { ...prev };
        delete next[riderId];
        return next;
      });
      setRiderCoverDrafts((prev) => {
        const next = { ...prev, [riderId]: String(res.value) };
        riderCoverDraftsLatestRef.current = {
          ...riderCoverDraftsLatestRef.current,
          [riderId]: String(res.value),
        };
        return next;
      });
      if (riderCovers[riderId] !== res.value) {
        handleRiderCoverChange(riderId, String(res.value));
      }
    },
    [selectedRiders, riderCovers, effectiveCoverAmount, handleRiderCoverChange],
  );

  const scheduleRiderCoverCommit = useCallback(
    (riderId) => {
      const timers = riderCoverDebounceTimersRef.current;
      if (timers[riderId]) clearTimeout(timers[riderId]);
      timers[riderId] = setTimeout(() => {
        delete timers[riderId];
        flushRiderCoverCommit(riderId);
      }, RIDER_COVER_PREMIUM_DEBOUNCE_MS);
    },
    [flushRiderCoverCommit],
  );

  const handleRiderCoverInputBlur = useCallback(
    (riderId) => {
      const timers = riderCoverDebounceTimersRef.current;
      if (timers[riderId]) {
        clearTimeout(timers[riderId]);
        delete timers[riderId];
      }
      flushRiderCoverCommit(riderId);
    },
    [flushRiderCoverCommit],
  );
const handleRiderCoverInputChange = useCallback(
  (riderId, value) => {
    markUserEditedQuote();

    riderCoverDraftsLatestRef.current = {
      ...riderCoverDraftsLatestRef.current,
      [riderId]: value,
    };

    setRiderCoverDrafts((prev) => ({ ...prev, [riderId]: value }));

    // 🔥 REAL-TIME VALIDATION
    const res = commitRiderCoverInput(
      value,
      riderId,
      effectiveCoverAmount,
      parseCoverAmountInput,
    );

    if (!res.ok) {
      setRiderCoverErrors((prev) => ({
        ...prev,
        [riderId]: res.error,
      }));
    } else {
      setRiderCoverErrors((prev) => {
        const next = { ...prev };
        delete next[riderId];
        return next;
      });
    }

    // keep debounce for premium API
    scheduleRiderCoverCommit(riderId);
  },
  [markUserEditedQuote, scheduleRiderCoverCommit, effectiveCoverAmount],
);
  //   (riderId, value) => {
  //     markUserEditedQuote();
  //     riderCoverDraftsLatestRef.current = {
  //       ...riderCoverDraftsLatestRef.current,
  //       [riderId]: value,
  //     };
  //     setRiderCoverErrors((prev) => {
  //       const next = { ...prev };
  //       delete next[riderId];
  //       return next;
  //     });
  //     setRiderCoverDrafts((prev) => ({ ...prev, [riderId]: value }));
  //     scheduleRiderCoverCommit(riderId);
  //   },
  //   [markUserEditedQuote, scheduleRiderCoverCommit],
  // );

  const resolveRiderCoversForSubmit = useCallback(() => {
    if (!effectiveCoverAmount) {
      return { error: "Please select or enter a cover amount.", covers: null };
    }
    const next = { ...riderCovers };
    for (const id of selectedRiders) {
      if (id === RIDER_ID_ADD_PLUS) {
        next[id] = effectiveCoverAmount;
        continue;
      }
      const raw =
        riderCoverDraftsLatestRef.current[id] ??
        riderCoverDrafts[id] ??
        (next[id] != null ? String(next[id]) : "");
      const res = commitRiderCoverInput(
        raw,
        id,
        effectiveCoverAmount,
        parseCoverAmountInput,
      );
      if (!res.ok) {
        const name = ridersData.find((r) => r.id === id)?.name ?? "Add-on";
        return { error: `${name}: ${res.error}`, covers: null };
      }
      next[id] = res.value;
    }
    return { error: null, covers: next };
  }, [
    effectiveCoverAmount,
    selectedRiders,
    riderCovers,
    riderCoverDrafts,
    ridersData,
  ]);

  const hasRiderInputErrors = useMemo(() => {
    for (const id of selectedRiders) {
      if (riderCoverErrors[id]) return true;
    }
    return false;
  }, [selectedRiders, riderCoverErrors]);

  const prevSelectedRidersRef = useRef(new Set());
  useEffect(() => {
    const prev = prevSelectedRidersRef.current;
    for (const id of selectedRiders) {
      if (!prev.has(id) && id !== RIDER_ID_ADD_PLUS) {
        flushRiderCoverCommit(id);
      }
    }
    prevSelectedRidersRef.current = new Set(selectedRiders);
  }, [selectedRiders, flushRiderCoverCommit]);

  return {
    riderCoverDrafts,
    riderCoverErrors,
    hasRiderInputErrors,
    handleRiderCoverInputChange,
    handleRiderCoverInputBlur,
    resolveRiderCoversForSubmit,
  };
}
